/**
 * Acute-first ordering for DDx clarification / refinement items (Phase 1).
 * Three bands: high-linked (urgent/cant-miss/red_flag dx) → unmapped → low-linked.
 * Optional refinement_aspect is preserved for analytics; it does not change sort order.
 */

/** @typedef {'context'|'history'|'exam'|'diagnostics'} RefinementAspect */

/**
 * @typedef {object} ClarificationItemInput
 * @property {string} question
 * @property {string[]} hint_keywords
 * @property {string[]} [pertains_to_conditions]
 * @property {RefinementAspect} [refinement_aspect]
 * @property {boolean} [fromDscr] - if true, prefix "[DSCR] " in toDisplayString
 */

/**
 * @param {unknown} s
 * @returns {string}
 */
function normLabel(s) {
  return String(s ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Likelihood ordinal for tie-breaking (higher = ranked more acute for sorting among non-urgent).
 * @param {unknown} likelihood
 * @returns {number}
 */
function likelihoodOrdinal(likelihood) {
  if (typeof likelihood === 'number' && !Number.isNaN(likelihood)) {
    return Math.max(0, Math.min(100, likelihood));
  }
  if (typeof likelihood === 'string') {
    const lower = likelihood.toLowerCase();
    if (lower.includes('more likely')) return 90;
    if (lower.includes('less likely')) return 10;
    if (lower.includes('genetic') || lower.includes('rare')) return 25;
    if (lower.includes('likely')) return 50;
  }
  return 0;
}

/**
 * Numeric acuity score for a diagnosis row on the current differential (higher = prioritize clarifications linked to it).
 * @param {object} d
 * @param {number} listIndex - order in merged list (0 = top); earlier = slight boost
 * @returns {number}
 */
export function scoreDiagnosisAcuityForClarifications(d, listIndex = 0) {
  if (!d || typeof d !== 'object') return 0;
  let score = 0;
  if (d.urgent === true) score += 10_000;
  if (d.isCantMiss === true) score += 5_000;
  const t = String(d.type || '').toLowerCase();
  if (t === 'red_flag') score += 3_000;
  score += likelihoodOrdinal(d.likelihood) * 10;
  score += Math.max(0, 200 - listIndex);
  return score;
}

/**
 * True if this diagnosis counts as "high acuity" for banding (clarifications linked here go in band 1).
 * @param {object} d
 * @returns {boolean}
 */
export function isHighAcuityDiagnosisForClarifications(d) {
  if (!d || typeof d !== 'object') return false;
  if (d.urgent === true) return true;
  if (d.isCantMiss === true) return true;
  const t = String(d.type || '').toLowerCase();
  if (t === 'red_flag') return true;
  return false;
}

/**
 * @param {object[]} diagnosisRows
 * @returns {Map<string, { row: object, index: number, score: number, high: boolean }>}
 */
function buildDiagnosisLookup(diagnosisRows) {
  const map = new Map();
  const rows = Array.isArray(diagnosisRows) ? diagnosisRows : [];
  rows.forEach((row, index) => {
    const label = row?.condition || row?.name;
    if (!label) return;
    const key = normLabel(label);
    if (!key) return;
    const score = scoreDiagnosisAcuityForClarifications(row, index);
    const high = isHighAcuityDiagnosisForClarifications(row);
    const prev = map.get(key);
    if (!prev || score > prev.score) {
      map.set(key, { row, index, score, high });
    }
  });
  return map;
}

/**
 * Match a single condition string from the model to a lookup key.
 * @param {string} cond
 * @param {Map<string, object>} lookup
 * @returns {{ score: number, high: boolean } | null}
 */
function matchOneCondition(cond, lookup) {
  const key = normLabel(cond);
  if (!key) return null;
  const hit = lookup.get(key);
  if (hit) return { score: hit.score, high: hit.high };
  for (const [k, v] of lookup.entries()) {
    if (key.includes(k) || k.includes(key)) {
      return { score: v.score, high: v.high };
    }
  }
  return null;
}

/**
 * @param {ClarificationItemInput} item
 * @param {Map<string, object>} lookup
 * @returns {{ band: 'high'|'low'|'unmapped', maxScore: number, originalIndex: number }}
 */
export function classifyClarificationItem(item, lookup, originalIndex) {
  const pertains = Array.isArray(item?.pertains_to_conditions) ? item.pertains_to_conditions : [];
  if (pertains.length === 0) {
    return { band: 'unmapped', maxScore: 0, originalIndex };
  }
  let maxScore = -1;
  let anyHigh = false;
  let anyMatch = false;
  for (const c of pertains) {
    const m = matchOneCondition(String(c), lookup);
    if (m) {
      anyMatch = true;
      if (m.score > maxScore) maxScore = m.score;
      if (m.high) anyHigh = true;
    }
  }
  if (!anyMatch) {
    return { band: 'unmapped', maxScore: 0, originalIndex };
  }
  if (anyHigh) {
    return { band: 'high', maxScore, originalIndex };
  }
  return { band: 'low', maxScore, originalIndex };
}

/**
 * @param {unknown} raw
 * @returns {ClarificationItemInput[]}
 */
export function normalizeClarificationItemsFromLlm(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const items = raw.clarification_items;
  if (Array.isArray(items)) {
    const out = [];
    for (const it of items) {
      if (typeof it === 'string' && it.trim()) {
        out.push({ question: it.trim(), hint_keywords: [], pertains_to_conditions: [] });
        continue;
      }
      if (!it || typeof it !== 'object') continue;
      const q = typeof it.question === 'string' ? it.question.trim() : '';
      if (!q) continue;
      const ptc = Array.isArray(it.pertains_to_conditions)
        ? it.pertains_to_conditions.map((x) => String(x).trim()).filter(Boolean)
        : [];
      const aspect = it.refinement_aspect;
      /** @type {ClarificationItemInput} */
      const entry = { question: q, hint_keywords: it.hint_keywords ?? [], pertains_to_conditions: ptc };
      if (
        aspect === 'context' ||
        aspect === 'history' ||
        aspect === 'exam' ||
        aspect === 'diagnostics'
      ) {
        entry.refinement_aspect = aspect;
      }
      out.push(entry);
    }
    if (out.length > 0) return out;
  }
  const legacyC = raw.clarifications;
  if (Array.isArray(legacyC)) {
    const legacyOut = [];
    for (const x of legacyC) {
      if (typeof x === 'string' && x.trim()) {
        legacyOut.push({ question: x.trim(), hint_keywords: [], pertains_to_conditions: [] });
        continue;
      }
      if (!x || typeof x !== 'object') continue;
      const q = typeof x.question === 'string' ? x.question.trim() : '';
      if (!q) continue;
      const ptc = Array.isArray(x.pertains_to_conditions)
        ? x.pertains_to_conditions.map((s) => String(s).trim()).filter(Boolean)
        : [];
      /** @type {ClarificationItemInput} */
      const entry = { question: q, hint_keywords: x.hint_keywords ?? [], pertains_to_conditions: ptc };
      const aspect = x.refinement_aspect;
      if (
        aspect === 'context' ||
        aspect === 'history' ||
        aspect === 'exam' ||
        aspect === 'diagnostics'
      ) {
        entry.refinement_aspect = aspect;
      }
      legacyOut.push(entry);
    }
    return legacyOut;
  }
  return [];
}

/**
 * Three-band sort: high-linked → unmapped (stable) → low-linked.
 * @param {ClarificationItemInput[]} items
 * @param {object[]} diagnosisRows
 * @returns {ClarificationItemInput[]}
 */
export function prioritizeClarificationItems(items, diagnosisRows) {
  const list = Array.isArray(items) ? [...items] : [];
  if (list.length === 0) return [];
  const lookup = buildDiagnosisLookup(diagnosisRows);

  const enriched = list.map((item, originalIndex) => ({
    item,
    ...classifyClarificationItem(item, lookup, originalIndex),
  }));

  const high = enriched.filter((e) => e.band === 'high');
  const unmapped = enriched.filter((e) => e.band === 'unmapped');
  const low = enriched.filter((e) => e.band === 'low');

  high.sort((a, b) => {
    if (b.maxScore !== a.maxScore) return b.maxScore - a.maxScore;
    return a.originalIndex - b.originalIndex;
  });
  unmapped.sort((a, b) => a.originalIndex - b.originalIndex);
  low.sort((a, b) => {
    if (b.maxScore !== a.maxScore) return b.maxScore - a.maxScore;
    return a.originalIndex - b.originalIndex;
  });

  return [...high, ...unmapped, ...low].map((e) => e.item);
}

/**
 * @param {ClarificationItemInput[]} items
 * @returns {string[]}
 */
export function clarificationItemsToQuestions(items) {
  return (Array.isArray(items) ? items : []).map((it) => {
    const q = typeof it.question === 'string' ? it.question : '';
    if (it.fromDscr && q && !/^\[DSCR\]/i.test(q.trim())) {
      return `[DSCR] ${q}`;
    }
    return q;
  });
}

/**
 * @param {ClarificationItemInput[]} items
 * @returns {{ question: string, hint_keywords?: string[], pertains_to_conditions: string[] }[]}
 */
export function clarificationItemsToRichItems(items) {
  return (Array.isArray(items) ? items : []).map((it) => {
    const q = typeof it.question === 'string' ? it.question : '';
    const pertains = Array.isArray(it.pertains_to_conditions)
      ? it.pertains_to_conditions
      : [];
    const hintKeywords = Array.isArray(it.hint_keywords)
      ? it.hint_keywords
      : [];
    const question =
      it.fromDscr && q && !/^\[DSCR\]/i.test(q.trim()) ? `[DSCR] ${q}` : q;
    const out = { question, pertains_to_conditions: pertains };
    if (hintKeywords.length > 0) {
      out.hint_keywords = hintKeywords;
    }
    return out;
  });
}

/**
 * @param {object} raw - LLM payload
 * @returns {{ step: string, pertains_to_conditions: string[], refinement_aspect?: string }[]}
 */
export function normalizeNextStepItemsFromLlm(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const items = raw.next_step_items;
  if (Array.isArray(items)) {
    const out = [];
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      const step = typeof it.step === 'string' ? it.step.trim() : '';
      if (!step) continue;
      const ptc = Array.isArray(it.pertains_to_conditions)
        ? it.pertains_to_conditions.map((x) => String(x).trim()).filter(Boolean)
        : [];
      const o = { step, pertains_to_conditions: ptc, refinement_aspect: 'diagnostics' };
      const asp = it.refinement_aspect;
      if (asp === 'context' || asp === 'history' || asp === 'exam' || asp === 'diagnostics') {
        o.refinement_aspect = asp;
      }
      out.push(o);
    }
    return out;
  }
  const legacy = raw.next_steps;
  if (Array.isArray(legacy)) {
    return legacy
      .map((x) => (typeof x === 'string' ? x.trim() : ''))
      .filter(Boolean)
      .map((step) => ({
        step,
        pertains_to_conditions: [],
        refinement_aspect: 'diagnostics',
      }));
  }
  return [];
}

/**
 * @param {{ step: string, pertains_to_conditions?: string[], refinement_aspect?: string }[]} stepItems
 * @param {object[]} diagnosisRows
 * @returns {{ step: string, pertains_to_conditions?: string[], refinement_aspect?: string }[]}
 */
export function prioritizeNextStepItems(stepItems, diagnosisRows) {
  const list = Array.isArray(stepItems) ? stepItems : [];
  if (list.length === 0) return [];
  const asQuestions = list.map((s) => ({
    question: s.step,
    hint_keywords: [],
    pertains_to_conditions: s.pertains_to_conditions || [],
    refinement_aspect: s.refinement_aspect,
  }));
  const sortedQ = prioritizeClarificationItems(asQuestions, diagnosisRows);
  return sortedQ.map((q) => ({
    step: q.question,
    pertains_to_conditions: q.pertains_to_conditions || [],
    refinement_aspect: q.refinement_aspect || 'diagnostics',
  }));
}

/**
 * @param {{ step: string }[]} items
 * @returns {string[]}
 */
export function nextStepItemsToStrings(items) {
  return (Array.isArray(items) ? items : []).map((x) => x.step);
}

/**
 * @param {object[]} suggestions - e.g. CalculatorSuggestion with triggeringDiagnosisId
 * @param {object[]} diagnosisRows
 * @returns {object[]}
 */
export function prioritizeCalculatorSuggestions(suggestions, diagnosisRows) {
  const list = Array.isArray(suggestions) ? [...suggestions] : [];
  if (list.length === 0) return [];

  const byDxId = new Map();
  (diagnosisRows || []).forEach((d, index) => {
    const id = String(d.diagnosisId || d.dxId || '')
      .trim()
      .toLowerCase();
    if (!id) return;
    const score = scoreDiagnosisAcuityForClarifications(d, index);
    const high = isHighAcuityDiagnosisForClarifications(d);
    const prev = byDxId.get(id);
    if (!prev || score > prev.score) {
      byDxId.set(id, { row: d, index, score, high });
    }
  });

  const enriched = list.map((s, originalIndex) => {
    const tid = String(s.triggeringDiagnosisId || '')
      .trim()
      .toLowerCase();
    const hit = tid ? byDxId.get(tid) : null;
    if (!hit) {
      return { s, band: 1, maxScore: 0, originalIndex };
    }
    const band = hit.high ? 0 : 2;
    return { s, band, maxScore: hit.score, originalIndex };
  });

  enriched.sort((a, b) => {
    if (a.band !== b.band) return a.band - b.band;
    if (b.maxScore !== a.maxScore) return b.maxScore - a.maxScore;
    return a.originalIndex - b.originalIndex;
  });

  return enriched.map((e) => e.s);
}

/**
 * @param {string[]} questions
 * @param {object[]} diagnosisRows
 * @returns {string[]}
 */
export function prioritizePlainQuestionStrings(questions, diagnosisRows) {
  const qs = Array.isArray(questions) ? questions : [];
  const items = qs.map((q) =>
    typeof q === 'string' && q.trim()
      ? { question: q.trim(), hint_keywords: [], pertains_to_conditions: [] }
      : null
  ).filter(Boolean);
  return clarificationItemsToQuestions(prioritizeClarificationItems(items, diagnosisRows));
}
